import type { Box, HousePlan, Lane, PathPoint, SeatGeom, TargetGeom } from './types';

/*
  Улицы дома и маршрут курьера (spec-3-motion §1–§3). Чистые функции: план дома — простые числа.

  Дом: коридор — левое поле меню (x = M.left + 8), порог — полоса между строкой знака и первым пунктом,
  крыша — полоса над карточками рамки (y = верх − 6), стены — середины зазоров 12 px слева от окон, полки —
  полосы 16 px над рядом цели. Ворота: «струя» (из ядра вверх на крышу), «угол» (из коридора через
  скругление меню на крышу), «порог» (с порога через правое поле меню в стену окна — только на главной).
  Углы соседей капля огибает дугой, концентричной их скруглению; повороты — только дугами.

  Рёбра — плотные выборки ~1 px с полосой, нормалью, местом и привязкой: так они режутся при пересборке
  и обращаются на обратном пути без своих формул. Путь ищет Дейкстра по рёбрам (на узле — только без
  излома курса); цена — длина плюс 60 px за каждые 90° поворота, при равной цене — меньше поворотов,
  затем выше по экрану. Граф строится заново при каждом вылете: та же раскладка — та же дорога.
*/

/* ----- Геометрия, px (spec-3-motion §12) ----- */

/** Крыша над верхом карточек. */
export const EDGE_GAP = 6;
/** Полка над рядом цели. */
export const SHELF_DY = 8;
/** Стена — в 6 px слева от кромки окна; дуга вокруг угла соседа: его скругление + 6 (16 + 6 = 22). */
export const CORNER_PAD = 6;
/** Дуга с порога в стену (ворота «порог») и спуск ядра на порог. */
export const SILL_R = 16;
/** Ворота «струя»: дуга из ядра на крышу. */
export const JET_R = 12;
/** Поворот со стены на полку; меньше, если иначе поднялся бы к полосе вкладок. */
export const SHELF_R = 24;
export const SHELF_R_MIN = 12;
/** Спуск на кромку — последние 40 px полки. */
export const GLIDE_PX = 40;
/** Посадка на блок: от левого (плечо) или правого (угол) края. */
export const SEAT_INSET = 34;
/** Посадка на вкладку: над её значком. */
export const TAB_SEAT_X = 20;
/** Сбоку от строки: зазор до неё (центр тела 16 px — в 14 px от края). */
export const SIDE_GAP = 6;
/** Цена поворота на 90°, px. */
export const TURN_COST = 60;
/** Коридор: от верха меню и над строкой «я». */
export const HALL_TOP = 24;
export const HALL_FOOT = 8;
/** Зазор, в котором помещается улица: крыша, стена. */
export const MIN_GAP = 12;
/** Поле страницы для посадки сбоку. */
export const SIDE_FIELD = 30;
/** Полоса вкладок: полка не поднимается выше её низа + 4. */
export const STRIP_CLEAR = 4;
/** Капля в пути. */
export const DROP = 8;

/** Свободный поперечник полос: коридор, крыша и стена — 12, порог и полка — 16. */
export const ROOM: Record<Lane, number> = {
  hall: 12,
  sill: 16,
  roof: 12,
  wall: 12,
  shelf: 16,
  glide: 16,
  gate: 12,
};

/** Наибольший излом курса на узле, рад: дальше — уже не стык улиц, а разворот. */
const JOIN_TOL = 0.35;
/** Шаг плотной выборки рёбер, px. */
const STEP = 1;

/* ----- Плотная выборка кусков пути ----- */

interface Pt {
  x: number;
  y: number;
}

type Normal = [number, number] | null;

interface Style {
  lane: Lane;
  normal: Normal;
  /** Привязка к рамке: число или ход от a до b по куску. */
  pin: number | [number, number];
}

const pinAt = (pin: Style['pin'], u: number) =>
  typeof pin === 'number' ? pin : pin[0] + (pin[1] - pin[0]) * u;

function pointOf(x: number, y: number, h: number, k: number, st: Style, u: number): PathPoint {
  return { x, y, h, k, lane: st.lane, normal: st.normal, room: ROOM[st.lane], pin: pinAt(st.pin, u) };
}

function line(a: Pt, b: Pt, st: Style): PathPoint[] {
  const len = Math.hypot(b.x - a.x, b.y - a.y);
  const n = Math.max(1, Math.ceil(len / STEP));
  const h = Math.atan2(b.y - a.y, b.x - a.x);
  const out: PathPoint[] = [];
  for (let i = 0; i <= n; i++) {
    const u = i / n;
    out.push(pointOf(a.x + (b.x - a.x) * u, a.y + (b.y - a.y) * u, h, 0, st, u));
  }
  return out;
}

/** Дуга окружности (экранные координаты, y вниз): угол a0 → a1; рост угла — поворот по часовой. */
function arc(c: Pt, r: number, a0: number, a1: number, st: Style): PathPoint[] {
  const sweep = a1 - a0;
  const n = Math.max(2, Math.ceil((Math.abs(sweep) * r) / STEP));
  const dir = Math.sign(sweep);
  const out: PathPoint[] = [];
  for (let i = 0; i <= n; i++) {
    const u = i / n;
    const th = a0 + sweep * u;
    out.push(
      pointOf(c.x + r * Math.cos(th), c.y + r * Math.sin(th), th + (dir * Math.PI) / 2, dir / r, st, u),
    );
  }
  return out;
}

/** Кривая по параметру u ∈ [0, 1] с производными — пересобирается в шаг ~1 px по длине. */
function curve(at: (u: number) => Pt, d1: (u: number) => Pt, d2: (u: number) => Pt, st: Style): PathPoint[] {
  const N = 400;
  const us: number[] = [0];
  const ss: number[] = [0];
  let prev = at(0);
  for (let i = 1; i <= N; i++) {
    const u = i / N;
    const p = at(u);
    ss.push(ss[i - 1] + Math.hypot(p.x - prev.x, p.y - prev.y));
    us.push(u);
    prev = p;
  }
  const len = ss[N];
  const n = Math.max(2, Math.ceil(len / STEP));
  const out: PathPoint[] = [];
  let j = 0;
  for (let i = 0; i <= n; i++) {
    const s = (len * i) / n;
    while (j < N - 1 && ss[j + 1] < s) j++;
    const f = ss[j + 1] > ss[j] ? (s - ss[j]) / (ss[j + 1] - ss[j]) : 0;
    const u = us[j] + (us[j + 1] - us[j]) * f;
    const p = at(u);
    const v = d1(u);
    const w = d2(u);
    const sp = Math.hypot(v.x, v.y) || 1e-9;
    out.push(pointOf(p.x, p.y, Math.atan2(v.y, v.x), (v.x * w.y - v.y * w.x) / (sp * sp * sp), st, u));
  }
  return out;
}

function cubic(p0: Pt, p1: Pt, p2: Pt, p3: Pt, st: Style): PathPoint[] {
  const at = (u: number) => {
    const m = 1 - u;
    return {
      x: m * m * m * p0.x + 3 * m * m * u * p1.x + 3 * m * u * u * p2.x + u * u * u * p3.x,
      y: m * m * m * p0.y + 3 * m * m * u * p1.y + 3 * m * u * u * p2.y + u * u * u * p3.y,
    };
  };
  const d1 = (u: number) => {
    const m = 1 - u;
    return {
      x: 3 * m * m * (p1.x - p0.x) + 6 * m * u * (p2.x - p1.x) + 3 * u * u * (p3.x - p2.x),
      y: 3 * m * m * (p1.y - p0.y) + 6 * m * u * (p2.y - p1.y) + 3 * u * u * (p3.y - p2.y),
    };
  };
  const d2 = (u: number) => ({
    x: 6 * (1 - u) * (p2.x - 2 * p1.x + p0.x) + 6 * u * (p3.x - 2 * p2.x + p1.x),
    y: 6 * (1 - u) * (p2.y - 2 * p1.y + p0.y) + 6 * u * (p3.y - 2 * p2.y + p1.y),
  });
  return curve(at, d1, d2, st);
}

/**
 * Спуск на кромку: y = a.y + (b.y − a.y)·(1 − cos πu)/2 — касательная горизонтальна в начале и в конце,
 * как у птицы, которая садится на провод.
 */
function glide(a: Pt, b: Pt, st: Style): PathPoint[] {
  const dx = b.x - a.x;
  const dy = b.y - a.y;
  return curve(
    (u) => ({ x: a.x + dx * u, y: a.y + (dy * (1 - Math.cos(Math.PI * u))) / 2 }),
    (u) => ({ x: dx, y: (dy * Math.PI * Math.sin(Math.PI * u)) / 2 }),
    (u) => ({ x: 0, y: (dy * Math.PI * Math.PI * Math.cos(Math.PI * u)) / 2 }),
    st,
  );
}

/** Склеить куски: стык общий, курс без скачков на 2π. */
export function join(...pieces: PathPoint[][]): PathPoint[] {
  const out: PathPoint[] = [];
  for (const piece of pieces) {
    if (piece.length === 0) continue;
    let shift = 0;
    if (out.length > 0) {
      const last = out[out.length - 1].h;
      shift = Math.round((last - piece[0].h) / (2 * Math.PI)) * 2 * Math.PI;
    }
    const start = out.length > 0 ? 1 : 0;
    for (let i = start; i < piece.length; i++)
      out.push(shift ? { ...piece[i], h: piece[i].h + shift } : piece[i]);
  }
  return out;
}

/** Тот же путь в обратную сторону: курс развёрнут, кривизна с другим знаком, нормали и привязка те же. */
export function reversed(pts: PathPoint[]): PathPoint[] {
  const out: PathPoint[] = [];
  for (let i = pts.length - 1; i >= 0; i--) out.push({ ...pts[i], h: pts[i].h + Math.PI, k: -pts[i].k });
  return out;
}

export function lengthOf(pts: PathPoint[]): number {
  let s = 0;
  for (let i = 1; i < pts.length; i++) s += Math.hypot(pts[i].x - pts[i - 1].x, pts[i].y - pts[i - 1].y);
  return s;
}

/* ----- Граф дома ----- */

export interface Edge {
  from: string;
  to: string;
  pts: PathPoint[];
  len: number;
  /** Суммарный поворот, рад. */
  turn: number;
  /** Сколько дуг (поворотов) на ребре. */
  bends: number;
  /** Σ y·ds — «выше по экрану» при равной цене. */
  area: number;
}

export interface Graph {
  nodes: Map<string, Pt>;
  out: Map<string, Edge[]>;
  /** Ось: улицы без направления — для рисунка «Показать дорогу». */
  streets: PathPoint[][];
  /** Узел цели (у пункта меню — узел коридора на его высоте). */
  seat: string | null;
  /** Почему цели нет на плане (посадка невозможна), если её просили. */
  refused: string | null;
}

function measure(pts: PathPoint[]): Pick<Edge, 'len' | 'turn' | 'bends' | 'area'> {
  let len = 0;
  let turn = 0;
  let bends = 0;
  let area = 0;
  let inBend = false;
  for (let i = 1; i < pts.length; i++) {
    const ds = Math.hypot(pts[i].x - pts[i - 1].x, pts[i].y - pts[i - 1].y);
    len += ds;
    area += ((pts[i].y + pts[i - 1].y) / 2) * ds;
    turn += Math.abs(pts[i].h - pts[i - 1].h);
    const bending = Math.abs(pts[i].k) > 1e-3;
    if (bending && !inBend) bends++;
    inBend = bending;
  }
  return { len, turn, bends, area };
}

function graph(): Graph {
  return { nodes: new Map(), out: new Map(), streets: [], seat: null, refused: null };
}

function addNode(g: Graph, id: string, p: Pt) {
  if (!g.nodes.has(id)) g.nodes.set(id, { x: p.x, y: p.y });
}

function addEdge(g: Graph, from: string, to: string, pts: PathPoint[]) {
  const e: Edge = { from, to, pts, ...measure(pts) };
  const list = g.out.get(from) ?? [];
  list.push(e);
  g.out.set(from, list);
}

/** Улица: ребро в обе стороны. */
function street(g: Graph, a: string, b: string, pts: PathPoint[]) {
  if (pts.length < 2) return;
  addNode(g, a, pts[0]);
  addNode(g, b, pts[pts.length - 1]);
  addEdge(g, a, b, pts);
  addEdge(g, b, a, reversed(pts));
  g.streets.push(pts);
}

const FRAME: Style = { lane: 'gate', normal: null, pin: 0 };
const HALL: Style = { lane: 'hall', normal: [1, 0], pin: 0 };
const SILL: Style = { lane: 'sill', normal: [0, -1], pin: 0 };
const ROOF: Style = { lane: 'roof', normal: [0, -1], pin: 0 };
const WALL: Style = { lane: 'wall', normal: [1, 0], pin: 1 };

const key = (prefix: string, v: number) => `${prefix}@${Math.round(v * 10) / 10}`;

/** Цепочка узлов вдоль прямой улицы: соседние соединены; pinAt — привязка в точке (по умолчанию из стиля). */
function chain(g: Graph, ids: { id: string; p: Pt }[], st: Style, pinAt?: (p: Pt) => number) {
  for (let i = 1; i < ids.length; i++) {
    const a = ids[i - 1].p;
    const b = ids[i].p;
    if (Math.hypot(b.x - a.x, b.y - a.y) < 0.5) continue;
    street(g, ids[i - 1].id, ids[i].id, line(a, b, pinAt ? { ...st, pin: [pinAt(a), pinAt(b)] } : st));
  }
}

/** Карточка, что стоит слева от окна вплотную по зазору (меню, уровень 2–3, первое окно). */
export function leftNeighbour(plan: HousePlan, pane: number) {
  const P = plan.panes[pane].box;
  let best: HousePlan['cards'][number] | null = null;
  for (const c of plan.cards) {
    if (c.kind === 'detail') continue; // Внутренняя панель не подменяет внешнюю стену соседнего окна.
    if (c.box.right > P.left + 0.5 || c.box.bottom <= P.top || c.box.top >= P.bottom) continue;
    if (!best || c.box.right > best.box.right) best = c;
  }
  return best;
}

export function roofOf(plan: HousePlan): number | null {
  const top = Math.min(...plan.cards.map((c) => c.box.top));
  return top - plan.ceiling >= MIN_GAP ? top - EDGE_GAP : null;
}

/**
 * Стена окна: x = P.left − 6 (spec-3-motion §1, §8) — держится за окно и тогда, когда сосед ещё едет (выезд и
 * смена зоны): стена от кромки соседа ушла бы с ним под зону. В покое зазор 12 px — та же середина зазора. Верх —
 * конец дуги вокруг угла соседа; cx — центр этой дуги (в покое концентрична скруглению соседа). null — зазора нет.
 */
export function wallOf(plan: HousePlan, pane: number) {
  const L = leftNeighbour(plan, pane);
  if (!L) return null;
  const left = plan.panes[pane].box.left;
  if (left - L.box.right < MIN_GAP) return null;
  const x = left - CORNER_PAD;
  return { x, top: L.box.top + L.radius, L, cx: x - CORNER_PAD - L.radius };
}

/* ----- Точки посадки (spec-3-motion §2) ----- */

const inside = (b: Box, outer: Box) =>
  b.left >= outer.left - 0.5 &&
  b.right <= outer.right + 0.5 &&
  b.top >= outer.top - 0.5 &&
  b.bottom <= outer.bottom + 0.5;

/** Где сядет тело и откуда подходит капля; строка — почему посадки нет. */
export function seatFor(plan: HousePlan, t: TargetGeom, mode: 'shoulder' | 'corner'): SeatGeom | string {
  if (t.kind === 'menu') {
    const item = plan.menu.items.find((i) => i.id === t.id);
    if (!item) return 'пункта нет в меню';
    if (!inside(item.box, plan.menu.scroll)) return 'пункт за краем прокрутки меню';
    const cy = (item.box.top + item.box.bottom) / 2;
    if (plan.home.kind === 'pill' && Math.abs(plan.home.y - cy) < 1) return 'цель — сам дом';
    return { kind: 'menu', x: plan.menu.hallX, y: cy, normal: [1, 0], pane: -1, shelfY: cy };
  }
  if (t.kind === 'panel') {
    const pane = plan.panes[t.pane];
    if (!pane || !inside(t.box, pane.box)) return 'панель за границей своего окна';
    const x = mode === 'corner' ? t.box.right - SEAT_INSET : t.box.left + SEAT_INSET;
    return {
      kind: 'zone',
      panel: true,
      x,
      y: t.box.top + 0.5,
      normal: [0, -1],
      pane: t.pane,
      shelfY: t.box.top,
    };
  }
  if (t.kind === 'zone') {
    // Справа от карточки меню приложения — стена окна (середина зазора): капля встаёт на ней против пункта
    // и прислоняется к нему, как в коридоре главного меню.
    const i = plan.panes.findIndex((_, k) => wallOf(plan, k)?.L.kind === 'zone');
    const w = i >= 0 ? wallOf(plan, i) : null;
    if (!w) return 'нет стены справа от меню приложения';
    if (!inside(t.box, w.L.box)) return 'пункт за краем меню приложения';
    const cy = (t.box.top + t.box.bottom) / 2;
    if (cy < w.top + 8 || cy > plan.panes[i].box.bottom - 8) return 'пункт вне стены';
    return { kind: 'zone', x: w.x, y: cy, normal: [-1, 0], pane: i, shelfY: cy };
  }
  const P = plan.panes[t.pane];
  if (!P) return 'окна цели нет на плане';
  if (t.kind === 'tab') {
    if (!inside(t.box, P.tabs)) return 'вкладка за краем полосы';
    return {
      kind: 'tab',
      x: t.box.left + TAB_SEAT_X,
      y: P.box.top + 0.5,
      normal: [0, -1],
      pane: t.pane,
      shelfY: P.box.top,
    };
  }
  const vis = P.scroll;
  const field = t.box.left - P.box.left;
  if (t.kind === 'row') {
    const y = (t.box.top + t.box.bottom) / 2;
    if (field < SIDE_FIELD) return 'поле страницы уже 30 px';
    if (y - 10 < vis.top || y + 10 > vis.bottom) return 'строка вне видимой части';
    return { kind: 'side', x: t.box.left - SIDE_GAP - 8, y, normal: [-1, 0], pane: t.pane, shelfY: y };
  }
  // Блок: верх виден — на верхнюю кромку (плечо или угол); ушёл под полосу вкладок — на левую кромку.
  if (t.box.top - SHELF_DY - 6 >= vis.top && t.box.top + 20 <= vis.bottom) {
    const x = mode === 'corner' ? t.box.right - SEAT_INSET : t.box.left + SEAT_INSET;
    return { kind: 'block', x, y: t.box.top, normal: [0, -1], pane: t.pane, shelfY: t.box.top - SHELF_DY };
  }
  if (t.box.top + 20 > vis.bottom) return 'блок ниже видимой части';
  const y = Math.max(t.box.top, vis.top) + SEAT_INSET;
  if (y + 15 > Math.min(t.box.bottom, vis.bottom)) return 'видна узкая полоска блока';
  if (field < SIDE_FIELD) return 'поле страницы уже 30 px';
  return { kind: 'side', x: t.box.left, y, normal: [-1, 0], pane: t.pane, shelfY: y };
}

/* ----- Сборка графа ----- */

/** Добавочные узлы улиц: коридор, стена окна и порог тянутся до них (пересборка вне улиц плана). */
export interface GraphExtra {
  hall?: number[];
  walls?: { pane: number; y: number }[];
  sill?: number[];
}

/**
 * Граф дома по плану; с посадкой — ещё и подход к ней (полка или спуск). Узлы 'home' и 'seat' — дом и
 * цель. Посадка, к которой улицы не ведут, — refused. extra — ещё узлы улиц (улицы тянутся до них).
 */
export function buildGraph(plan: HousePlan, seat: SeatGeom | null = null, extra: GraphExtra = {}): Graph {
  const g = graph();
  const M = plan.menu.box;
  const hx = plan.menu.hallX;
  const sy = plan.menu.sillY;
  const roofY = roofOf(plan);
  const { hallTop, hallBottom } = plan.menu;
  const home = plan.home;
  addNode(g, 'home', home);

  const hallYs = new Map<number, string>();
  const hallNode = (y: number, id?: string) => {
    const yy = Math.round(y * 10) / 10;
    if (yy < hallTop - 0.5 || yy > hallBottom + 0.5) return null;
    const name = hallYs.get(yy) ?? id ?? key('hall', yy);
    hallYs.set(yy, name);
    addNode(g, name, { x: hx, y: yy });
    return name;
  };

  for (const y of extra.hall ?? []) hallNode(y);

  // Стены окон и что к ним ведёт.
  const walls = plan.panes.map((_, i) => wallOf(plan, i));
  const wallYs = walls.map(() => new Map<number, string>());
  const wallNode = (i: number, y: number) => {
    const w = walls[i]!;
    const yy = Math.round(y * 10) / 10;
    const name = wallYs[i].get(yy) ?? key(`wall${i}`, yy);
    wallYs[i].set(yy, name);
    addNode(g, name, { x: w.x, y: yy });
    return name;
  };

  // Ворота «порог» — только когда справа от меню сразу окно (главная). Порог растягивается вместе с меню: у
  // коридора он стоит, у ворот едет с правой кромкой (меню сворачивают или раскрывают, пока капля на пороге, —
  // она остаётся внутри меню).
  const gateWall = plan.menu.sillGate
    ? walls.findIndex((w) => w && w.L.kind === 'menu' && Math.abs(w.x - (M.right + CORNER_PAD)) < 1)
    : -1;
  const gateX = M.right - 10;
  const sillPin = (p: Pt) =>
    gateWall >= 0 ? Math.min(1, Math.max(0, (p.x - (hx + 8)) / Math.max(1, gateX - (hx + 8)))) : 0;

  // Ядро в знаке: вниз S-кривой в коридор, каплей на порог, струёй на крышу.
  if (home.kind === 'mark') {
    const { x: cx, y: cy } = home;
    const hall = hallNode(cy + 40);
    if (hall)
      street(
        g,
        'home',
        hall,
        cubic({ x: cx, y: cy }, { x: cx, y: cy + 20 }, { x: hx, y: cy + 16 }, { x: hx, y: cy + 40 }, FRAME),
      );
    if (sy - SILL_R > cy) {
      street(
        g,
        'home',
        'sill:mark',
        join(
          line({ x: cx, y: cy }, { x: cx, y: sy - SILL_R }, FRAME),
          arc({ x: cx + SILL_R, y: sy - SILL_R }, SILL_R, Math.PI, Math.PI / 2, {
            ...FRAME,
            pin: [0, sillPin({ x: cx + SILL_R, y: sy })],
          }),
        ),
      );
    }
    if (roofY !== null && roofY + JET_R < cy) {
      street(
        g,
        'home',
        'roof:jet',
        join(
          line({ x: cx, y: cy }, { x: cx, y: roofY + JET_R }, FRAME),
          arc({ x: cx + JET_R, y: roofY + JET_R }, JET_R, Math.PI, (3 * Math.PI) / 2, FRAME),
        ),
      );
    }
  } else {
    // Выделение: влево в коридор S-кривой, вверх или вниз. Пункт сразу под порогом — ещё и вход с узла под
    // ним (sy + 8): иначе из порога и из знака капля проскакивает вход сверху и без разворота не попадает.
    const { y: py } = home;
    const ys = [py - 28, py + 28];
    if (py - (sy + 8) >= 16 && py - (sy + 8) < 28) ys.push(sy + 8);
    for (const y of ys) {
      const hall = hallNode(y);
      if (hall) street(g, 'home', hall, homeExit(plan, home, y));
    }
  }

  // Порог: стык с коридором дугами R 8, спуск ядра, ворота «порог» в стену окна справа.
  const sillNodes: { id: string; p: Pt }[] = [{ id: 'sill:w', p: { x: hx + 8, y: sy } }];
  const up = hallNode(sy - 8);
  const down = hallNode(sy + 8);
  if (up) street(g, up, 'sill:w', arc({ x: hx + 8, y: sy - 8 }, 8, Math.PI, Math.PI / 2, FRAME));
  if (down) street(g, down, 'sill:w', arc({ x: hx + 8, y: sy + 8 }, 8, Math.PI, (3 * Math.PI) / 2, FRAME));
  if (home.kind === 'mark' && g.nodes.has('sill:mark'))
    sillNodes.push({ id: 'sill:mark', p: g.nodes.get('sill:mark')! });
  for (const x of extra.sill ?? []) sillNodes.push({ id: key('sill', x), p: { x, y: sy } });

  if (gateWall >= 0) {
    const i = gateWall;
    {
      sillNodes.push({ id: 'sill:gate', p: { x: M.right - 10, y: sy } });
      // Ворота «порог» едут с правой кромкой меню и окном (их зазор постоянен): привязка — к окну.
      street(
        g,
        'sill:gate',
        wallNode(i, sy + SILL_R),
        arc({ x: M.right - 10, y: sy + SILL_R }, SILL_R, -Math.PI / 2, 0, { ...FRAME, pin: 1 }),
      );
    }
  }
  sillNodes.sort((a, b) => a.p.x - b.p.x);
  chain(g, sillNodes, SILL, sillPin);

  // Крыша: ворота «угол» из коридора, углы соседей, спуск к вкладке.
  const roofNodes: { id: string; p: Pt }[] = [];
  if (roofY !== null) {
    const top = hallNode(hallTop);
    if (top) {
      street(
        g,
        top,
        'roof:corner',
        cubic(
          { x: hx, y: hallTop },
          { x: hx, y: hallTop - 20 },
          { x: hx + 4, y: roofY },
          { x: hx + 20, y: roofY },
          FRAME,
        ),
      );
      roofNodes.push({ id: 'roof:corner', p: { x: hx + 20, y: roofY } });
    }
    if (g.nodes.has('roof:jet')) roofNodes.push({ id: 'roof:jet', p: g.nodes.get('roof:jet')! });
    walls.forEach((w, i) => {
      if (!w) return;
      const L = w.L;
      const cx = w.cx;
      const id = `roof:L${i}`;
      roofNodes.push({ id, p: { x: cx, y: roofY } });
      street(
        g,
        id,
        wallNode(i, L.box.top + L.radius),
        arc({ x: cx, y: L.box.top + L.radius }, L.radius + CORNER_PAD, -Math.PI / 2, 0, {
          ...FRAME,
          pin: [0, 1],
        }),
      );
    });
  }

  for (const w of extra.walls ?? []) if (walls[w.pane]) wallNode(w.pane, w.y);

  // Подход к посадке.
  if (seat) {
    if (seat.kind === 'menu') {
      g.seat = hallNode(seat.y, 'seat');
      if (!g.seat) g.refused = 'пункт вне коридора';
    } else if (seat.kind === 'zone' && seat.panel) {
      if (roofY === null) g.refused = 'нет крыши над панелью';
      else {
        roofNodes.push({ id: 'roof:panel', p: { x: seat.x - GLIDE_PX, y: roofY } });
        g.seat = 'seat';
        street(
          g,
          'roof:panel',
          'seat',
          glide(
            { x: seat.x - GLIDE_PX, y: roofY },
            { x: seat.x, y: seat.y },
            { lane: 'glide', normal: [0, -1], pin: 2 },
          ),
        );
      }
    } else if (seat.kind === 'zone') {
      if (walls[seat.pane]) g.seat = wallNode(seat.pane, seat.y);
      else g.refused = 'нет стены у меню приложения';
    } else if (seat.kind === 'tab') {
      if (roofY === null) g.refused = 'нет крыши над рамкой';
      else {
        const x0 = seat.x - GLIDE_PX;
        roofNodes.push({ id: 'roof:tab', p: { x: x0, y: roofY } });
        g.seat = 'seat';
        street(
          g,
          'roof:tab',
          'seat',
          glide({ x: x0, y: roofY }, { x: seat.x, y: seat.y }, { lane: 'glide', normal: [0, -1], pin: 2 }),
        );
      }
    } else {
      const w = walls[seat.pane];
      const P = plan.panes[seat.pane];
      if (!w) g.refused = 'нет стены у окна цели';
      else {
        const sh = seat.shelfY;
        const r = Math.min(SHELF_R, sh - (P.strip.bottom + STRIP_CLEAR));
        const top = Math.min(...[...wallYs[seat.pane].keys()]);
        if (r < SHELF_R_MIN) g.refused = 'полка у полосы вкладок';
        else if (Number.isFinite(top) && sh - r < top) g.refused = 'полка выше стены';
        else if (seat.x - (w.x + r) < 4) g.refused = 'посадка у самой стены';
        else {
          const gx = Math.max(w.x + r, seat.x - GLIDE_PX);
          const approach = seat.kind === 'side' ? seat.normal : ([0, -1] as [number, number]);
          g.seat = 'seat';
          street(
            g,
            wallNode(seat.pane, sh - r),
            'seat',
            join(
              arc({ x: w.x + r, y: sh - r }, r, Math.PI, Math.PI / 2, { ...FRAME, pin: [1, 2] }),
              gx > w.x + r + 0.5
                ? line({ x: w.x + r, y: sh }, { x: gx, y: sh }, { lane: 'shelf', normal: [0, -1], pin: 2 })
                : [],
              glide({ x: gx, y: sh }, { x: seat.x, y: seat.y }, { lane: 'glide', normal: approach, pin: 2 }),
            ),
          );
        }
      }
    }
  }

  roofNodes.sort((a, b) => a.p.x - b.p.x);
  chain(g, roofNodes, ROOF);
  walls.forEach((w, i) => {
    if (!w) return;
    const ys = [...wallYs[i].entries()].sort((a, b) => a[0] - b[0]);
    chain(
      g,
      ys.map(([y, id]) => ({ id, p: { x: w.x, y } })),
      WALL,
    );
  });
  const hall = [...hallYs.entries()].sort((a, b) => a[0] - b[0]);
  chain(
    g,
    hall.map(([y, id]) => ({ id, p: { x: hx, y } })),
    HALL,
  );
  return g;
}

/* ----- Поиск пути ----- */

export interface Route {
  pts: PathPoint[];
  length: number;
  bends: number;
  /** Узлы по порядку — для подписи и тестов. */
  nodes: string[];
}

type Cost = [number, number, number];
const less = (a: Cost, b: Cost) =>
  a[0] < b[0] - 1e-6 ||
  (Math.abs(a[0] - b[0]) <= 1e-6 && (a[1] < b[1] || (a[1] === b[1] && a[2] < b[2] - 1e-6)));

const angleGap = (a: number, b: number) => {
  const d = Math.abs(((a - b) % (2 * Math.PI)) + 2 * Math.PI) % (2 * Math.PI);
  return Math.min(d, 2 * Math.PI - d);
};

/**
 * Дейкстра по рёбрам: на узле путь продолжается только без излома курса (стык улиц), поэтому разворот
 * на месте невозможен. heading — с каким курсом капля уже идёт из начала (null — стоит).
 */
export function findRoute(g: Graph, from: string, to: string, heading: number | null = null): Route | null {
  const edges: Edge[] = [];
  for (const list of g.out.values()) edges.push(...list);
  const index = new Map(edges.map((e, i) => [e, i]));
  const dist: (Cost | null)[] = edges.map(() => null);
  const prev: (number | null)[] = edges.map(() => null);
  const done = edges.map(() => false);
  const cost = (e: Edge): Cost => [e.len + (TURN_COST * e.turn) / (Math.PI / 2), e.bends, e.area];
  for (const e of g.out.get(from) ?? []) {
    if (heading !== null && angleGap(heading, e.pts[0].h) > JOIN_TOL) continue;
    dist[index.get(e)!] = cost(e);
  }
  let goal = -1;
  for (;;) {
    let best = -1;
    for (let i = 0; i < edges.length; i++) {
      if (done[i] || !dist[i]) continue;
      if (best < 0 || less(dist[i]!, dist[best]!)) best = i;
    }
    if (best < 0) break;
    done[best] = true;
    const e = edges[best];
    if (e.to === to) {
      goal = best;
      break;
    }
    const end = e.pts[e.pts.length - 1].h;
    for (const n of g.out.get(e.to) ?? []) {
      if (angleGap(end, n.pts[0].h) > JOIN_TOL) continue;
      const j = index.get(n)!;
      if (done[j]) continue;
      const c = cost(n);
      const d: Cost = [dist[best]![0] + c[0], dist[best]![1] + c[1], dist[best]![2] + c[2]];
      if (!dist[j] || less(d, dist[j]!)) {
        dist[j] = d;
        prev[j] = best;
      }
    }
  }
  if (goal < 0) return null;
  const chainIdx: number[] = [];
  for (let i: number | null = goal; i !== null; i = prev[i]) chainIdx.unshift(i);
  const pts = join(...chainIdx.map((i) => edges[i].pts));
  return {
    pts,
    length: lengthOf(pts),
    bends: chainIdx.reduce((n, i) => n + edges[i].bends, 0),
    nodes: [from, ...chainIdx.map((i) => edges[i].to)],
  };
}

/**
 * Встать на граф из произвольной точки (пересборка в пути): ближайшая точка рёбер не дальше maxDist —
 * ребро режется, появляется узел 'here' с выходами в обе стороны. null — улиц рядом нет.
 */
export function attachPoint(g: Graph, x: number, y: number, maxDist = 8, here = 'here'): string | null {
  let best: { e: Edge; i: number; d: number } | null = null;
  for (const list of g.out.values()) {
    for (const e of list) {
      for (let i = 0; i < e.pts.length; i++) {
        const d = Math.hypot(e.pts[i].x - x, e.pts[i].y - y);
        if (d <= maxDist && (!best || d < best.d - 1e-9)) best = { e, i, d };
      }
    }
  }
  if (!best) return null;
  const { e, i } = best;
  addNode(g, here, e.pts[i]);
  if (i < e.pts.length - 1) addEdge(g, here, e.to, e.pts.slice(i));
  if (i > 0) addEdge(g, here, e.from, reversed(e.pts.slice(0, i + 1)));
  return here;
}

/**
 * «Есть ли улица графа ближе r px» — по ячейкам r × r, без перебора всех рёбер на каждую точку (граф не
 * меняется; после attachPoint индекс строится заново).
 */
export function streetsNear(g: Graph, r: number): (x: number, y: number) => boolean {
  const cells = new Map<string, Pt[]>();
  const cell = (x: number, y: number) => `${Math.floor(x / r)}:${Math.floor(y / r)}`;
  for (const list of g.out.values()) {
    for (const e of list) {
      for (const p of e.pts) {
        const k = cell(p.x, p.y);
        const c = cells.get(k);
        if (c) c.push(p);
        else cells.set(k, [p]);
      }
    }
  }
  return (x, y) => {
    for (let dx = -1; dx <= 1; dx++) {
      for (let dy = -1; dy <= 1; dy++) {
        for (const p of cells.get(cell(x + dx * r, y + dy * r)) ?? []) {
          if ((p.x - x) * (p.x - x) + (p.y - y) * (p.y - y) <= r * r) return true;
        }
      }
    }
    return false;
  };
}

/**
 * Капля стоит вне улиц плана (цель ушла со страницы, посадка уехала, дом сменился): над окном — выход как подход
 * к цели наоборот, полкой на своей высоте к стене этого окна (посадка — в самой капле); у стены окна — стена
 * тянется до капли; на пороге меню — порог. null — ни то, ни другое.
 */
export function exitOf(
  plan: HousePlan,
  x: number,
  y: number,
): { seat: SeatGeom | null; extra: GraphExtra } | null {
  const pane = plan.panes.findIndex(
    (P) => x > P.box.left && x < P.box.right && y > P.box.top && y < P.box.bottom,
  );
  if (pane >= 0) return { seat: { kind: 'block', x, y, normal: [0, -1], pane, shelfY: y }, extra: {} };
  for (let i = 0; i < plan.panes.length; i++) {
    const w = wallOf(plan, i);
    if (w && Math.abs(x - w.x) < 12 && y > w.top && y < plan.panes[i].box.bottom)
      return { seat: null, extra: { walls: [{ pane: i, y }] } };
  }
  const { menu } = plan;
  if (Math.abs(y - menu.sillY) < 6 && x > menu.hallX + 8 && x < menu.box.right)
    return { seat: null, extra: { sill: [x] } };
  return null;
}

/**
 * Выход к стене (exitOf): полка от капли до стены едет за окном всё больше — у капли она держится за цель
 * (привязка 2), у стены — за окно (1). Окно, сдвинутое выехавшей зоной, не уводит стену из-под полки.
 */
export function easeExit(pts: PathPoint[]): PathPoint[] {
  const j = pts.findIndex((p) => p.pin <= 1);
  if (j <= 0) return pts;
  const L = lengthOf(pts.slice(0, j + 1));
  let s = 0;
  return pts.map((p, i) => {
    if (i >= j) return p;
    if (i > 0) s += Math.hypot(p.x - pts[i - 1].x, p.y - pts[i - 1].y);
    return { ...p, pin: L > 0 ? 2 - s / L : 1 };
  });
}

/**
 * Дом сменился, пока ядро было в отлучке: путь от прежнего дома к нынешнему. Выходы прежнего дома (как их
 * строит план с ним) встают на граф нового — каждый режет улицу, в которую приходит. heading — с каким курсом
 * капля входит в прежний дом (null — стоит в нём). Без разворота не выйти — первым же выходом до улицы плана,
 * там разворот (в пути — точка возврата: splitCusps режет по ней). null — прежний дом с планом не связан.
 */
export function routeFromOld(
  plan: HousePlan,
  from: HousePlan['home'],
  heading: number | null,
): PathPoint[] | null {
  const old = buildGraph({ ...plan, home: from });
  // Коридор нового плана — до концов выходов прежнего дома (иначе он кончается у последнего своего узла).
  const ends = (old.out.get('home') ?? []).map((e) => e.pts[e.pts.length - 1]);
  const g = buildGraph(plan, null, {
    hall: ends.filter((p) => Math.abs(p.x - plan.menu.hallX) < 0.5).map((p) => p.y),
  });
  const exits: Edge[] = [];
  for (const e of old.out.get('home') ?? []) {
    const end = e.pts[e.pts.length - 1];
    const id = attachPoint(g, end.x, end.y, 2, `from:${exits.length}`);
    if (!id) continue;
    addNode(g, 'from', from);
    addEdge(g, 'from', id, e.pts);
    exits.push({ ...e, to: id });
  }
  const direct = exits.length > 0 ? findRoute(g, 'from', 'home', heading) : null;
  if (direct) return direct.pts;
  // Дома в одном столбце меню: выход прежнего прямо на вход нового (с курсом — разворот в прежнем доме).
  const h = hop(plan, from);
  if (h) return h;
  const any = exits.length > 0 && heading !== null ? findRoute(g, 'from', 'home') : null;
  if (any) return any.pts;
  let best: PathPoint[] | null = null;
  for (const e of exits) {
    const r = findRoute(g, e.to, 'home');
    if (r && (!best || e.len + r.length < lengthOf(best))) best = join(e.pts, r.pts);
  }
  return best;
}

/** S-кривая из дома в коридор на высоту y — как выходы buildGraph (знак — вниз, выделение — влево и вверх или вниз). */
function homeExit(plan: HousePlan, h: HousePlan['home'], y: number): PathPoint[] {
  const hx = plan.menu.hallX;
  const d = y - h.y;
  return h.kind === 'mark'
    ? cubic({ x: h.x, y: h.y }, { x: h.x, y: h.y + d / 2 }, { x: hx, y: h.y + 0.4 * d }, { x: hx, y }, FRAME)
    : cubic(
        { x: h.x, y: h.y },
        { x: h.x - 18, y: h.y },
        { x: hx, y: h.y + (10 / 28) * d },
        { x: hx, y },
        FRAME,
      );
}

/**
 * Прямой переход между домами в меню (знак и выделения стоят в одном столбце): выход прежнего дома в коридор,
 * по коридору, вход в новый — без разворота. Дома близко (первый пункт под знаком, соседние пункты) — выход и
 * вход встречаются посередине. null — не помещается (S-кривая короче 12 px, коридор кончился).
 */
export function hop(plan: HousePlan, from: HousePlan['home']): PathPoint[] | null {
  const to = plan.home;
  const s = Math.sign(to.y - from.y);
  if (s === 0 || (from.kind === 'mark' && s < 0) || (to.kind === 'mark' && s > 0)) return null;
  const reach = (h: HousePlan['home']) => (h.kind === 'mark' ? 40 : 28);
  let ya = from.y + s * reach(from);
  let yb = to.y - s * reach(to);
  if (s * (yb - ya) < 0) ya = yb = (from.y + to.y) / 2;
  if (Math.abs(ya - from.y) < 12 || Math.abs(to.y - yb) < 12) return null;
  const { hallTop, hallBottom, hallX } = plan.menu;
  if (Math.min(ya, yb) < hallTop - 0.5 || Math.max(ya, yb) > hallBottom + 0.5) return null;
  return join(
    homeExit(plan, from, ya),
    Math.abs(yb - ya) > 0.5 ? line({ x: hallX, y: ya }, { x: hallX, y: yb }, HALL) : [],
    reversed(homeExit(plan, to, yb)),
  );
}

/** Путь, разрезанный по точкам возврата (курс поворачивает больше чем на 90° между соседними точками). */
export function splitCusps(pts: PathPoint[]): PathPoint[][] {
  const out: PathPoint[][] = [];
  let cur: PathPoint[] = [];
  for (const p of pts) {
    const last = cur[cur.length - 1];
    if (last && angleGap(p.h, last.h) > Math.PI / 2) {
      out.push(cur);
      // Разворот на месте: следующий кусок — с той же точки, уже новым курсом.
      cur = [{ ...last, h: p.h, k: 0 }];
    }
    cur.push(p);
  }
  out.push(cur);
  return out.filter((piece) => piece.length > 1 && lengthOf(piece) > 0.5);
}

/** Последние px пути привязать к цели (пункт меню при прокрутке nav). */
export function pinTail(pts: PathPoint[], px: number, pin = 2): PathPoint[] {
  const out = pts.map((p) => ({ ...p }));
  let s = 0;
  for (let i = out.length - 1; i > 0 && s < px; i--) {
    out[i].pin = Math.max(out[i].pin, pin * (1 - s / px));
    s += Math.hypot(out[i].x - out[i - 1].x, out[i].y - out[i - 1].y);
  }
  return out;
}
